<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import KanbanColumnsApi from 'dashboard/api/kanbanColumns';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  column: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['saved', 'close']);

const { t } = useI18n();

const PALETA = [
  '#3B82F6',
  '#F59E0B',
  '#8B5CF6',
  '#10B981',
  '#EF4444',
  '#EC4899',
  '#14B8A6',
  '#6B7280',
];

const dialogRef = ref(null);
const name = ref(props.column?.name || '');
const color = ref(props.column?.color || PALETA[0]);
const isSaving = ref(false);

const isEditing = computed(() => Boolean(props.column?.id));
const title = computed(() =>
  isEditing.value ? t('KANBAN.EDIT_COLUMN') : t('KANBAN.NEW_COLUMN')
);

const onConfirm = async () => {
  if (!name.value.trim()) return;
  isSaving.value = true;
  try {
    const payload = { name: name.value.trim(), color: color.value };
    if (isEditing.value) {
      await KanbanColumnsApi.update(props.column.id, {
        kanban_column: payload,
      });
    } else {
      await KanbanColumnsApi.create({ kanban_column: payload });
    }
    emit('saved');
  } catch (error) {
    useAlert(t('KANBAN.UPDATE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

onMounted(() => dialogRef.value?.open());
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="title"
    :confirm-button-label="$t('KANBAN.SAVE')"
    :disable-confirm-button="!name.trim()"
    :is-loading="isSaving"
    width="sm"
    show-cancel-button
    @confirm="onConfirm"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="name"
        :label="$t('KANBAN.COLUMN_NAME')"
        :placeholder="$t('KANBAN.COLUMN_NAME_PLACEHOLDER')"
      />
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('KANBAN.COLUMN_COLOR') }}
        </label>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="option in PALETA"
            :key="option"
            type="button"
            class="size-7 rounded-full border-2 transition-transform"
            :class="
              color === option
                ? 'scale-110 border-n-slate-12'
                : 'border-transparent'
            "
            :style="{ backgroundColor: option }"
            @click="color = option"
          />
        </div>
      </div>
    </div>
  </Dialog>
</template>
